import { useEffect, useState } from "react";
import { Share2, Copy, Video } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { fill, shareOut, copyOut, personalLink } from "@/lib/partnerKit";
import { Section, Chips } from "@/components/partner/Section";

const INTENTS = [["story", "Tell my story"], ["curious", "Make people curious"], ["recommend", "Recommend Xcelio"], ["gift", "Gift something useful"]];
const INTENT_SUB = { story: "Why I discovered Xcelio.", curious: "A short hook without overexplaining.", recommend: "A straightforward personal recommendation.", gift: "Send someone a free Reset." };
const CHANNELS = [["instagram", "Instagram"], ["story", "Story"], ["tiktok", "TikTok"], ["whatsapp", "WhatsApp"], ["linkedin", "LinkedIn / X"], ["email", "Email"]];

export default function ShareStudio({ k }) {
  const [intent, setIntent] = useState("story");
  const [channel, setChannel] = useState("instagram");
  const vals = { name: k.name, link: personalLink(k.slug, channel), code: k.code };
  const base = fill(k.copy.share?.[intent]?.[channel] || "", vals);
  const [text, setText] = useState(base);
  useEffect(() => setText(base), [base]);
  const pickIntent = (i) => { if (i === "gift") { document.getElementById("gift")?.scrollIntoView({ behavior: "smooth" }); return; } setIntent(i); };
  const log = { intent, channel };

  return (
    <Section id="share" eyebrow="Share" title="What would you like to share today?">
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        {INTENTS.map(([key, label]) => (
          <button key={key} onClick={() => pickIntent(key)} data-testid={`share-intent-${key}`}
            className={`rounded-2xl border p-4 text-left transition-colors duration-200 ${intent === key ? "border-primary bg-primary/[0.08]" : "border-border bg-card/40 hover:border-primary/40"}`}>
            <span className="block font-serif text-lg">{label}</span>
            <span className="block text-xs text-muted-foreground mt-0.5">{INTENT_SUB[key]}</span>
          </button>
        ))}
      </div>
      <Chips items={CHANNELS} value={channel} onPick={setChannel} testPrefix="share-channel" />
      <div className="mt-5 rounded-3xl border border-border bg-card/40 p-5">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={channel === "email" || channel === "instagram" ? 9 : 4}
          className="bg-transparent border-0 p-0 focus-visible:ring-0 resize-none leading-relaxed" data-testid="share-text" />
        <div className="flex gap-2 mt-4">
          <button onClick={() => shareOut({ text, log })} className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm" data-testid="share-share-btn"><Share2 className="w-4 h-4" /> Share</button>
          <button onClick={() => copyOut(text, log)} className="inline-flex items-center gap-1.5 rounded-full border border-border px-5 py-2.5 text-sm" data-testid="share-copy-btn"><Copy className="w-4 h-4" /> Copy</button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">Your personal link is already in it. Add #ad when you post.</p>
      </div>
      <div className="mt-8" data-testid="video-hooks">
        <p className="flex items-center gap-2 text-sm font-medium mb-3"><Video className="w-4 h-4 text-primary" /> Video hooks: say one of these to camera, then tell your own story</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {(k.copy.hooks || []).map((h, i) => (
            <button key={i} onClick={() => copyOut(h)} className="rounded-xl border border-border bg-card/30 px-4 py-3 text-left font-serif italic hover:border-primary/40 transition-colors duration-200" data-testid={`video-hook-${i}`}>"{h}"</button>
          ))}
        </div>
      </div>
    </Section>
  );
}
